import { Button } from '@shared/components/ui/button';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { Input } from '@shared/components/ui/input';
import { useToast } from '@shared/hooks/use-toast';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdjustWarehouseMaterialStock } from '../../hooks/useAdjustWarehouseMaterialStock';
import { useWarehouseMaterialEmptyLocations } from '../../hooks/useWarehouseMaterialEmptyLocations';

export interface AddStockToLocationModalRef {
  open: (productId: number, onConfirm?: () => void) => void;
}

export const AddStockToLocationModal = forwardRef<AddStockToLocationModalRef>((_, ref) => {
  const { t } = useTranslation('materialAlmacenModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmRef = useRef<() => void>(() => {});
  const { toast } = useToast();

  const [productId, setProductId] = useState<number | null>(null);
  const [locationId, setLocationId] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [reason, setReason] = useState('');

  const isOpen = productId != null;
  const { data: emptyLocations = [], isLoading } = useWarehouseMaterialEmptyLocations(isOpen);
  const { mutateAsync: adjustStock, isPending } = useAdjustWarehouseMaterialStock();

  const locationOptions = useMemo(
    () =>
      emptyLocations.map((location) => ({
        value: String(location.id),
        label: location.code,
      })),
    [emptyLocations],
  );

  useImperativeHandle(ref, () => ({
    open: (id, onConfirm) => {
      setProductId(id);
      setLocationId('');
      setQuantity(0);
      setReason('');
      confirmRef.current = onConfirm || (() => {});
      modalRef.current?.openModal();
    },
  }));

  const handleConfirm = async () => {
    if (!productId || !locationId || quantity <= 0) {
      toast({
        title: t('modal.adjust_stock.error_title'),
        description: t('modal.adjust_stock.validation_error'),
        variant: 'destructive',
      });
      return;
    }

    if (!reason.trim()) {
      toast({
        title: t('modal.adjust_stock.error_title'),
        description: t('modal.adjust_stock.error_no_reason'),
        variant: 'destructive',
      });
      return;
    }

    try {
      await adjustStock({
        product_id: productId,
        quantity,
        location_id: Number(locationId),
      });

      toast({
        title: t('modal.adjust_stock.success_title'),
        description: t('modal.adjust_stock.success_description'),
        variant: 'success',
      });

      confirmRef.current();
      modalRef.current?.closeModal();
    } catch (error: unknown) {
      const err = error as { response?: { data?: Record<string, unknown> }; message?: string };
      const responseData = err?.response?.data;
      const apiMessage =
        (responseData?.message as string | undefined) ||
        (responseData?.error as { message?: string } | undefined)?.message ||
        err?.message;

      toast({
        title: t('modal.adjust_stock.error_title'),
        description: apiMessage || t('errors.unexpected'),
        variant: 'destructive',
      });
    }
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={t('modal.locations_stock.assign_location')}
      type="default"
      width="lg"
      fullScreenMobile
    >
      <div className="space-y-6 pt-4">
        <p className="text-sm text-subtext">{t('modal.locations_stock.add_location_helper')}</p>

        <div>
          <label className="block text-sm mb-2 text-text">{t('modal.adjust_stock.location_label')}</label>
          <Combobox
            options={locationOptions}
            placeholder={
              isLoading
                ? t('modal.adjust_stock.locations_loading')
                : locationOptions.length === 0
                  ? t('modal.adjust_stock.locations_empty')
                  : t('modal.adjust_stock.location_placeholder')
            }
            value={locationId}
            setValue={setLocationId}
            disabled={isLoading || locationOptions.length === 0}
            dataTestId="warehouse-material-add-stock-location"
          />
        </div>

        <div className="max-w-[150px]">
          <label className="block text-sm mb-2 text-text">{t('modal.adjust_stock.quantity_label')}</label>
          <StepperInput value={quantity} onChange={setQuantity} min={0} max={99999} />
        </div>

        <div>
          <label className="block text-sm mb-2 text-text">{t('modal.adjust_stock.reason_label')}</label>
          <Input
            placeholder={t('modal.adjust_stock.reason_placeholder')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" size="lg" onClick={() => modalRef.current?.closeModal()} disabled={isPending}>
            {t('button.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleConfirm}
            disabled={!locationId || quantity <= 0 || isPending}
          >
            {isPending ? t('button.saving') : t('button.confirm')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

AddStockToLocationModal.displayName = 'AddStockToLocationModal';
